import React, { useCallback, useEffect, useRef } from 'react'
import { Animated, LayoutChangeEvent, View } from 'react-native'
import { useScrollState } from '../../utils/ui'

export function Reveal({ children, delay = 0, offset = 18 }: { children: React.ReactNode; delay?: number; offset?: number }) {
  const opacity = useRef(new Animated.Value(0)).current
  const translate = useRef(new Animated.Value(offset)).current
  const layoutY = useRef<number | null>(null)
  const layoutHeight = useRef(0)
  const hasAnimated = useRef(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const viewRef = useRef<View | null>(null)
  const { scrollY, viewportHeight, contentRef } = useScrollState()

  const runAnimation = useCallback(() => {
    if (hasAnimated.current) {
      return
    }

    hasAnimated.current = true
    timerRef.current = setTimeout(() => {
      Animated.parallel([
        Animated.timing(opacity, {
          toValue: 1,
          duration: 420,
          useNativeDriver: true,
        }),
        Animated.timing(translate, {
          toValue: 0,
          duration: 420,
          useNativeDriver: true,
        }),
      ]).start()
    }, delay)
  }, [delay, opacity, translate])

  const maybeReveal = useCallback(
    (scrollOffset = 0) => {
      if (hasAnimated.current || layoutY.current === null || viewportHeight <= 0) {
        return
      }

      const triggerLine = scrollOffset + viewportHeight * 0.88
      const elementTop = layoutY.current
      const elementBottom = elementTop + layoutHeight.current

      if (triggerLine >= elementTop && scrollOffset <= elementBottom) {
        return runAnimation()
      }
    },
    [runAnimation, viewportHeight]
  )

  useEffect(() => {
    if (!scrollY.addListener) {
      return maybeReveal(0)
    }

    maybeReveal(scrollY.__getValue ? scrollY.__getValue() : 0)

    const listenerId = scrollY.addListener(({ value }) => {
      maybeReveal(value)
    })

    return () => {
      if (listenerId && scrollY.removeListener) {
        scrollY.removeListener(listenerId)
      }
    }
  }, [maybeReveal, scrollY])

  const handleLayout = useCallback(
    (event: LayoutChangeEvent) => {
      layoutHeight.current = event.nativeEvent.layout.height

      const contentNode = contentRef?.current
      const viewNode = viewRef.current

      if (contentNode && viewNode && typeof viewNode.measureLayout === 'function') {
        viewNode.measureLayout(
          contentNode,
          (_x: number, y: number, _width: number, heightValue: number) => {
            layoutY.current = y
            layoutHeight.current = heightValue
            maybeReveal(scrollY.__getValue ? scrollY.__getValue() : 0)
          },
          () => {
            layoutY.current = event.nativeEvent.layout.y
            maybeReveal(scrollY.__getValue ? scrollY.__getValue() : 0)
          }
        )
        return
      }

      layoutY.current = event.nativeEvent.layout.y
      maybeReveal(scrollY.__getValue ? scrollY.__getValue() : 0)
    },
    [contentRef, maybeReveal, scrollY]
  )

  useEffect(() => {
    if (viewportHeight > 0) {
      maybeReveal(scrollY.__getValue ? scrollY.__getValue() : 0)
    }
  }, [maybeReveal, scrollY, viewportHeight])

  useEffect(() => {
    if (viewportHeight <= 0) {
      runAnimation()
    }
  }, [runAnimation, viewportHeight])

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
    }
  }, [])

  return (
    <Animated.View
      ref={viewRef}
      onLayout={handleLayout}
      style={{
        opacity,
        transform: [{ translateY: translate }],
      }}
    >
      {children}
    </Animated.View>
  )
}
